/**
 * domains/attribution/attributionJournal.ts — MER Algo Footprints v8.2.0
 *
 * Lightweight NDJSON journal for attribution snapshots and outcomes.
 *
 * Design:
 *   - In-process memory index (primary store, works in browser and Node)
 *   - NDJSON file persistence (server/Node only — silently skipped in browser)
 *   - File layout: ./data/attribution/{snapshots,outcomes}/YYYY-MM-DD.ndjson
 *   - No Redis dependency — standalone, inspectable, easy to reset
 *
 * Browser safety:
 *   The `fs` and `path` modules are Node-only. All file I/O is guarded by
 *   IS_NODE so webpack/CRA never tries to bundle them. In the browser the
 *   module works purely from the in-process memory index.
 *
 * Lifecycle:
 *   pending   → snapshot written, no outcome yet
 *   evaluated → outcome appended, snapshot status updated in memory
 *   expired   → set during cleanup if TTL passed without evaluation
 *
 * SAFETY: This module does NOT modify any engine. It is purely additive.
 */

import type {
  AttributionSnapshot,
  AttributionOutcome,
  AttributionRecord,
} from './types';

// ─── Runtime environment guard ────────────────────────────────────────────────
// Detect Node.js at runtime — never import fs/path at the module level so
// webpack/CRA does not attempt to bundle or polyfill them.

const IS_NODE =
  typeof process !== 'undefined' &&
  typeof process.versions === 'object' &&
  typeof process.versions.node === 'string';

// Lazy-loaded Node modules — undefined in browser
let _fs:   typeof import('fs')   | undefined;
let _path: typeof import('path') | undefined;

function getFs(): typeof import('fs') | undefined {
  if (!IS_NODE) return undefined;
  if (!_fs) {
    try { _fs = require('fs'); } catch { /* not available */ }
  }
  return _fs;
}

function getPath(): typeof import('path') | undefined {
  if (!IS_NODE) return undefined;
  if (!_path) {
    try { _path = require('path'); } catch { /* not available */ }
  }
  return _path;
}

// ─── Paths ────────────────────────────────────────────────────────────────────

function getDataBase(): string {
  const p = getPath();
  return p ? p.join(process.cwd(), 'data', 'attribution') : '';
}

function getSnapshotsDir(): string {
  const p = getPath();
  return p ? p.join(getDataBase(), 'snapshots') : '';
}

function getOutcomesDir(): string {
  const p = getPath();
  return p ? p.join(getDataBase(), 'outcomes') : '';
}

const SNAPSHOT_TTL_MS = 8 * 60 * 60 * 1000;   // 8 hours

// ─── In-process index ─────────────────────────────────────────────────────────

const _index = new Map<string, AttributionRecord>();
let   _indexLoaded = false;

// ─── Directory helpers ────────────────────────────────────────────────────────

function ensureDirs(): void {
  const fs = getFs();
  if (!fs) return;
  try { fs.mkdirSync(getSnapshotsDir(), { recursive: true }); } catch (_) {}
  try { fs.mkdirSync(getOutcomesDir(),  { recursive: true }); } catch (_) {}
}

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function snapshotFile(date = todayDate()): string {
  const p = getPath();
  return p ? p.join(getSnapshotsDir(), `${date}.ndjson`) : '';
}

function outcomeFile(date = todayDate()): string {
  const p = getPath();
  return p ? p.join(getOutcomesDir(), `${date}.ndjson`) : '';
}

// ─── Low-level NDJSON I/O ─────────────────────────────────────────────────────

function appendLine(filePath: string, obj: unknown): void {
  const fs = getFs();
  if (!fs || !filePath) return;   // browser: silent no-op
  try {
    ensureDirs();
    fs.appendFileSync(filePath, JSON.stringify(obj) + '\n', 'utf8');
  } catch (err: any) {
    console.warn('[AttributionJournal] append failed:', err.message);
  }
}

function readAllLines(filePath: string): unknown[] {
  const fs = getFs();
  if (!fs || !filePath) return [];
  try {
    if (!fs.existsSync(filePath)) return [];
    const raw = fs.readFileSync(filePath, 'utf8');
    return raw
      .split('\n')
      .filter(l => l.trim())
      .map(l => { try { return JSON.parse(l); } catch { return null; } })
      .filter(Boolean) as unknown[];
  } catch {
    return [];
  }
}

// ─── Index management ─────────────────────────────────────────────────────────

export function loadIndex(daysBack = 7): void {
  _index.clear();
  ensureDirs();

  for (let d = 0; d <= daysBack; d++) {
    const date = new Date(Date.now() - d * 86_400_000).toISOString().slice(0, 10);
    const lines = readAllLines(snapshotFile(date)) as AttributionRecord[];
    for (const record of lines) {
      if (record?.snapshot?.snapshotId) {
        _index.set(record.snapshot.snapshotId, record);
      }
    }
  }

  for (let d = 0; d <= daysBack; d++) {
    const date = new Date(Date.now() - d * 86_400_000).toISOString().slice(0, 10);
    const lines = readAllLines(outcomeFile(date)) as Array<{
      snapshotId: string; outcome: AttributionOutcome; evaluatedAt: number;
    }>;
    for (const entry of lines) {
      const record = _index.get(entry.snapshotId);
      if (record) {
        record.status      = 'evaluated';
        record.evaluatedAt = entry.evaluatedAt;
        record.outcome     = entry.outcome;
      }
    }
  }

  _indexLoaded = true;
}

function ensureIndex(): void {
  if (!_indexLoaded) loadIndex();
}

// ─── Write operations ─────────────────────────────────────────────────────────

export function recordSnapshot(snapshot: AttributionSnapshot): AttributionRecord {
  ensureIndex();

  const existing = _index.get(snapshot.snapshotId);
  if (existing) return existing;

  const record: AttributionRecord = {
    snapshot,
    status:     'pending',
    recordedAt: Date.now(),
  };

  _index.set(snapshot.snapshotId, record);
  appendLine(snapshotFile(), record);
  return record;
}

export function recordOutcome(outcome: AttributionOutcome): void {
  ensureIndex();

  const record = _index.get(outcome.snapshotId);
  if (!record) {
    console.warn('[AttributionJournal] recordOutcome — snapshot not found:', outcome.snapshotId);
    return;
  }
  if (record.status === 'evaluated') return;

  const now = Date.now();
  record.status      = 'evaluated';
  record.evaluatedAt = now;
  record.outcome     = outcome;

  appendLine(outcomeFile(), { snapshotId: outcome.snapshotId, evaluatedAt: now, outcome });
}

export function markExpiredSnapshots(): number {
  ensureIndex();
  const cutoff = Date.now() - SNAPSHOT_TTL_MS;
  let count = 0;
  for (const record of _index.values()) {
    if (record.status === 'pending' && record.recordedAt < cutoff) {
      record.status = 'expired';
      count++;
    }
  }
  return count;
}

// ─── Read operations ──────────────────────────────────────────────────────────

export function getRecord(snapshotId: string): AttributionRecord | undefined {
  ensureIndex();
  return _index.get(snapshotId);
}

export function getPendingSnapshots(): AttributionRecord[] {
  ensureIndex();
  return [..._index.values()].filter(r => r.status === 'pending');
}

export function getEvaluatedRecords(): AttributionRecord[] {
  ensureIndex();
  return [..._index.values()].filter(r => r.status === 'evaluated');
}

export function getAllRecords(): AttributionRecord[] {
  ensureIndex();
  return [..._index.values()];
}

export function getLatestRecord(): AttributionRecord | undefined {
  ensureIndex();
  let latest: AttributionRecord | undefined;
  for (const r of _index.values()) {
    if (!latest || r.recordedAt > latest.recordedAt) latest = r;
  }
  return latest;
}

// ─── Reset / test helpers ─────────────────────────────────────────────────────

export function resetIndex(): void {
  _index.clear();
  _indexLoaded = true;
}

export function clearJournalFiles(): void {
  resetIndex();
  const fs = getFs();
  const p  = getPath();
  if (!fs || !p) return;   // browser: no-op
  try {
    const sd = getSnapshotsDir();
    const od = getOutcomesDir();
    const sdFiles = fs.existsSync(sd) ? fs.readdirSync(sd).filter(f => f.endsWith('.ndjson')) : [];
    const odFiles = fs.existsSync(od) ? fs.readdirSync(od).filter(f => f.endsWith('.ndjson')) : [];
    for (const f of sdFiles) fs.unlinkSync(p.join(sd, f));
    for (const f of odFiles) fs.unlinkSync(p.join(od, f));
  } catch (err: any) {
    console.warn('[AttributionJournal] clearJournalFiles:', err.message);
  }
}

// ─── Journal metadata ─────────────────────────────────────────────────────────

export interface JournalStats {
  total:     number;
  pending:   number;
  evaluated: number;
  expired:   number;
}

export function getJournalStats(): JournalStats {
  ensureIndex();
  const records = [..._index.values()];
  return {
    total:     records.length,
    pending:   records.filter(r => r.status === 'pending').length,
    evaluated: records.filter(r => r.status === 'evaluated').length,
    expired:   records.filter(r => r.status === 'expired').length,
  };
}

export const _paths = { getSnapshotsDir, getOutcomesDir, snapshotFile, outcomeFile };
